import { Navigate, createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBackTo } from '@/lib/use-back';
import { Loader2, MessageSquareQuote, PenLine, TicketPercent } from 'lucide-react';
import { api, type ReviewFilter } from '@/lib/api';
import { IconChip, SectionLabel } from '@/components/ui/calm';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonParagraph,
} from '@/components/ui/skeleton';
import { RatingSummary } from '@/components/reviews/RatingSummary';
import { ReviewCard } from '@/components/reviews/ReviewCard';
import { ReviewForm } from '@/components/reviews/ReviewForm';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/telegram';
import { formatNumber } from '@/lib/date';
import type { Review } from '@/api/types';

export const Route = createFileRoute('/reviews')({
  // `leave=<id подписки>` приходит с кнопки «Оставить отзыв» в карточке подписки в
  // боте: форма открывается сразу и на этой подписке.
  validateSearch: (search: Record<string, unknown>): { leave?: string } =>
    typeof search.leave === 'string' && search.leave ? { leave: search.leave } : {},
  component: Reviews,
});

function Reviews() {
  useBackTo('/profile');
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<ReviewFilter>('all');
  const [extra, setExtra] = useState<Review[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const { leave } = Route.useSearch();
  const leaveHandled = useRef(false);
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['reviews', filter],
    queryFn: async () => {
      const res = await api.reviews({ filter });
      // Смена фильтра начинает список заново: дозагруженные страницы прошлой
      // вкладки к новой не относятся.
      setExtra([]);
      setCursor(res.next_cursor);
      return res;
    },
    placeholderData: keepPreviousData,
  });

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await api.reviews({ filter, cursor });
      setExtra((prev) => [...prev, ...next.items]);
      setCursor(next.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  const filters: { key: ReviewFilter; label: string; count?: number }[] = [
    { key: 'all', label: t('Filter all'), count: data?.summary.total },
    { key: 'positive', label: t('Reviews filter good'), count: data?.summary.positive },
    { key: 'negative', label: t('Reviews filter bad'), count: data?.summary.negative },
  ];

  const items = [...(data?.items ?? []), ...extra];
  // Сколько карточек было на экране до последней дозагрузки: от него считается задержка
  // появления новых. Смена фильтра начинает список заново, тогда отсчёт с нуля.
  const shownBefore = useRef(0);
  const pageStart = items.length > shownBefore.current ? shownBefore.current : 0;
  useEffect(() => {
    shownBefore.current = items.length;
  });
  const promo = data?.config.promo;
  const targets = data?.review_targets ?? [];
  // Есть хоть одна неоценённая покупка — зовём написать, иначе предлагаем поправить.
  const canLeaveNew = targets.some((x) => !x.reviewed);
  // Скрытый отзыв править нельзя: бэк примет правку, но на витрину его не вернёт.
  // Если все покупки в таком состоянии, кнопки быть не должно вовсе — вместо неё
  // объяснение, иначе экран выглядит сломанным.
  const actionable = targets.filter((x) => !x.reviewed || !x.hidden);
  const allHidden = targets.length > 0 && actionable.length === 0;

  // Открываем форму один раз, когда подписка из ссылки нашлась среди доступных.
  // Скрытый партнёром отзыв правке не подлежит, для него формы не будет.
  useEffect(() => {
    if (!leave || leaveHandled.current || !data) return;
    leaveHandled.current = true;
    if (actionable.some((x) => x.subscription_id === leave)) setFormOpen(true);
  }, [leave, data, actionable]);

  if (isPending && !data) return <ReviewsSkeleton />;

  // Партнёр выключил раздел: вкладки в навигации уже нет, поэтому сюда можно попасть
  // только по старой ссылке из истории чата. Уводим на главную вместо экрана-заглушки,
  // который выглядел как поломка.
  if (data && !data.enabled) {
    return <Navigate to="/me" replace />;
  }

  return (
    <div className="p-4 pb-24 space-y-5">
      <header className="px-2">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] text-foreground">{t('Reviews')}</h2>
        {data && data.summary.total > 0 && (
          <p className="mt-2 text-sm text-text-body flex items-center gap-2 font-medium">
            <span
              className="w-1.5 h-1.5 rounded-full bg-success shrink-0"
              
              aria-hidden
            />
            {t('Reviews positive share', { percent: data.summary.positive_share })}
          </p>
        )}
      </header>

      {promo?.enabled && (
        <div
          className="flex items-center gap-3 rounded-[18px] px-4 py-3.5 border"
          style={{ background: 'var(--t-surface)', borderColor: 'var(--t-line)' }}
        >
          <div
            className="w-[34px] h-[34px] shrink-0 rounded-[11px] grid place-items-center text-white"
            style={{ background: 'var(--calm-cta)', boxShadow: 'var(--calm-cta-shadow)' }}
          >
            <TicketPercent size={17} />
          </div>
          <div className="min-w-0">
            {/* Текст пишет партнёр, и обещание скидки обрезалось на «получите скидку 1…». */}
            <div className="text-[13.5px] font-bold leading-snug line-clamp-2 text-foreground">{promo.title}</div>
            {promo.subtitle && <div className="text-[11.5px] text-text-muted font-medium line-clamp-2">{promo.subtitle}</div>}
          </div>
        </div>
      )}

      {data && data.summary.total > 0 && <RatingSummary summary={data.summary} />}

      {/* Кнопка есть, только если человеку есть что оценить: оплаченная подписка.
          Написан отзыв за текущий цикл — предлагаем его поправить. */}
      {allHidden && (
        <div className="calm-card rounded-[16px] px-4 py-3.5 text-center">
          <div className="text-sm font-bold text-foreground">{t('Review hidden')}</div>
          <div className="mt-1 text-[12px] text-text-muted">{t('Review hidden desc')}</div>
        </div>
      )}

      {actionable.length > 0 && (
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setFormOpen(true);
          }}
          className="w-full calm-cta rounded-[var(--r-btn)] py-3.5 text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
        >
          <PenLine size={16} />
          {canLeaveNew ? t('Reviews leave') : t('Reviews edit')}
        </button>
      )}

      {/* Ряд прокручивается вбок, а не растягивает страницу: у сервиса с тысячами
          отзывов три пилюли с числами шире 320px, и весь экран уезжал вбок. */}
      <div className="flex gap-2 -mx-4 px-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {filters.map((f) => (
          <button
            type="button"
            key={f.key}
            onClick={() => {
              haptic('light');
              setFilter(f.key);
            }}
            className={cn(
              // На 320px три пилюли с числами упирались в самый край экрана.
              'shrink-0 whitespace-nowrap px-3 min-[360px]:px-4 py-1.5 rounded-full text-xs font-semibold transition-transform duration-100 active:scale-[0.97] flex items-center gap-1.5',
              filter === f.key ? 'border-transparent bg-foreground text-background' : 'calm-card text-text-muted',
            )}
          >
            {f.label}
            {f.count !== undefined && <span className="tabular opacity-70">{formatNumber(f.count, i18n.language)}</span>}
          </button>
        ))}
      </div>

      {items.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <SectionLabel>{t('Reviews latest')}</SectionLabel>
          <span className="text-xs text-text-muted font-semibold tabular">
            {t('Shown of total', { shown: items.length, total: data?.summary.total ?? 0 })}
          </span>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <EmptyBlock title={t('Reviews empty')} text={t('Reviews empty desc')} />
        ) : (
          // Задержка считается от начала дозагруженной порции: иначе каждая карточка
          // второй страницы ждала 400 мс невидимой и потом появлялась вся пачка разом.
          items.map((review, i) => <ReviewCard key={review.id} review={review} index={i - pageStart} />)
        )}
      </div>

      {cursor && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full calm-card rounded-[18px] py-3.5 text-sm font-semibold text-text-body active:scale-[0.99] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loadingMore && <Loader2 size={16} className="animate-spin" />}
          {loadingMore ? t('Loading') : t('Show more')}
        </button>
      )}

      <p className="text-center text-[11px] text-text-muted font-medium pt-1">{t('Reviews verified note')}</p>

      {targets.length > 0 && (
        <ReviewForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          targets={targets}
          initialTargetId={leave}
          onSaved={() => {
            setExtra([]);
            void queryClient.invalidateQueries({ queryKey: ['reviews'] });
          }}
        />
      )}
    </div>
  );
}

function EmptyBlock({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center calm-card rounded-[var(--r-card)]">
      <IconChip icon={MessageSquareQuote} tone="blue" size={64} radius={18} className="mb-4" />
      <h3 className="font-semibold mb-1 text-foreground">{title}</h3>
      <p className="text-sm text-text-muted">{text}</p>
    </div>
  );
}

/**
 * Заголовок настоящий, дальше — сводка рейтинга и карточки отзывов: ник с датой,
 * звёзды, текст. Раньше тут стояли три плиты, и список после загрузки прыгал.
 */
function ReviewsSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-5">
      <header className="px-2 space-y-2">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] text-foreground">{t('Reviews')}</h2>
        <SkeletonLine size="sm" className="w-48" />
      </header>
      <SkeletonCard className="rounded-[var(--r-card)] min-h-0 p-5 flex items-center gap-5">
        <div className="space-y-2">
          <SkeletonLine size="hero" className="w-14" />
          <SkeletonLine size="xs" className="w-16" />
        </div>
        <div className="flex-1 space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-2 w-full rounded-full" />
          ))}
        </div>
      </SkeletonCard>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[22px] min-h-0 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0 space-y-2">
              <SkeletonLine className="w-1/3" />
              <SkeletonLine size="xs" className="w-2/5" />
            </div>
            <div className="flex gap-0.5 shrink-0">
              {[0, 1, 2, 3, 4].map((star) => (
                <Skeleton key={star} className="w-3.5 h-3.5 rounded-sm" />
              ))}
            </div>
          </div>
          <SkeletonParagraph lines={2} size="sm" />
        </SkeletonCard>
      ))}
    </SkeletonPage>
  );
}
